'use client';

import React, { useState, useEffect } from 'react';
import { financeService } from '../../services/database/supabase';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, CheckCircle } from 'lucide-react';

interface GoalManagerProps {
  userId: string;
}

export default function GoalManager({ userId }: GoalManagerProps) {
  const [goals, setGoals] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    current_amount: '',
    target_amount: '',
    deadline: ''
  });

  // Carregar metas
  useEffect(() => {
    loadGoals();
  }, []);

  const loadGoals = async () => {
    try {
      const data = await financeService.getGoals();
      setGoals(data);
    } catch (error) {
      toast.error('Erro ao carregar metas');
    }
  };

  // Funções do formulário
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      current_amount: '',
      target_amount: '',
      deadline: ''
    });
    setEditingGoal(null);
  };

  // CRUD operações
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const goalData = {
        name: formData.title,
        description: formData.description,
        current: parseFloat(formData.current_amount) || 0,
        target: parseFloat(formData.target_amount) || 0,
        deadline: formData.deadline,
        color: '#' + Math.floor(Math.random()*16777215).toString(16), // Cor aleatória
        category: 'Geral'
      };

      if (editingGoal) {
        await financeService.updateGoal(editingGoal.id, goalData);
        toast.success('Meta atualizada com sucesso!');
      } else {
        await financeService.createGoal(goalData);
        toast.success('Meta criada com sucesso!');
      }

      loadGoals();
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Erro ao salvar meta');
      console.error(error);
    }
  };

  const handleEdit = (goal: any) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.name,
      description: goal.description || '',
      current_amount: goal.current?.toString() || '0',
      target_amount: goal.target?.toString() || '0',
      deadline: goal.deadline || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta meta?')) {
      try {
        await financeService.deleteGoal(id);
        toast.success('Meta excluída com sucesso!');
        loadGoals();
      } catch (error) {
        toast.error('Erro ao excluir meta');
      }
    }
  };

  const handleComplete = async (id: string) => {
    try {
      await financeService.completeGoal(id);
      toast.success('Meta marcada como concluída!');
      loadGoals();
    } catch (error) {
      toast.error('Erro ao concluir meta');
    }
  };

  // Cálculo de progresso
  const calculateProgress = (current: number, target: number) => {
    return Math.min((current / target) * 100, 100);
  };

  // Formatação de valor
  const formatValue = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-6 border border-white/[0.08]">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Minhas Metas</h2>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all duration-200 shadow-lg shadow-white/25"
        >
          <Plus className="w-4 h-4" />
          Nova Meta
        </button>
      </div>

      {/* Lista de Metas */}
      <div className="space-y-4">
        {goals.map((goal) => (
          <div
            key={goal.id}
            className="p-4 bg-white/[0.02] rounded-lg border border-white/[0.05]"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-lg font-medium text-white">{goal.name}</h3>
                <p className="text-sm text-gray-400">{goal.description}</p>
              </div>
              <div className="flex items-center gap-2">
                {goal.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleEdit(goal)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-white/[0.1] rounded-lg transition-all duration-200"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleComplete(goal.id)}
                      className="p-2 text-gray-400 hover:text-green-500 hover:bg-green-500/[0.1] rounded-lg transition-all duration-200"
                    >
                      <CheckCircle size={16} />
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleDelete(goal.id)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-500/[0.1] rounded-lg transition-all duration-200"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {/* Barra de Progresso */}
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">
                  {formatValue(goal.current)} de {formatValue(goal.target)}
                </span>
                <span className={goal.status === 'completed' ? 'text-green-500' : 'text-blue-500'}>
                  {goal.status === 'completed' ? 'Concluída' : 'Em andamento'}
                </span>
              </div>
              <div className="w-full h-2 bg-white/[0.05] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    goal.status === 'completed' ? 'bg-green-500' : 'bg-blue-600'
                  }`}
                  style={{
                    width: `${calculateProgress(goal.current, goal.target)}%`
                  }}
                />
              </div>
              <div className="mt-2 text-xs text-gray-400">
                Prazo: {new Date(goal.deadline).toLocaleDateString('pt-BR')}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Criar/Editar Meta */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#111] rounded-lg p-6 w-full max-w-md border border-white/[0.1]">
            <h3 className="text-xl font-semibold text-white mb-4">
              {editingGoal ? 'Editar Meta' : 'Nova Meta'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Título
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-lg text-white focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Descrição
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-lg text-white focus:outline-none focus:border-blue-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Valor Atual
                </label>
                <input
                  type="number"
                  name="current_amount"
                  value={formData.current_amount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-lg text-white focus:outline-none focus:border-blue-500"
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Valor Alvo
                </label>
                <input
                  type="number"
                  name="target_amount"
                  value={formData.target_amount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-lg text-white focus:outline-none focus:border-blue-500"
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Prazo
                </label>
                <input
                  type="date"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-lg text-white focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all duration-200 shadow-lg shadow-white/25"
                >
                  {editingGoal ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
